//
// scenarios.ts — canned timelines the user can load with a single
// click. Useful for (a) developer testing without hand-building a
// timeline every time, and (b) a "try this" entry point for users
// who haven't used the recorder before.
//
// Each scenario is a complete specification: an initial view,
// actions on one or more tracks, total duration, and optional
// fade envelope. Loading a scenario replaces the panel's current
// state — see handleLoadScenario in Panel.tsx.
//
import {
  createAction,
  type Action,
  type LayerFade,
  type MapView,
  type OverlayConfig,
  type PanAction,
  type PitchAction,
  type RotateAction,
  type ZoomAction,
} from './timeline';

export interface Scenario {
  //
  // Short, button-friendly name (shown on the load button).
  //
  name: string;
  //
  // Longer description used as the button's tooltip.
  //
  description: string;
  initialView: MapView;
  actions: Action[];
  totalSeconds: number;
  fadeIn?: number;
  fadeOut?: number;
  //
  // Which HTML overlays to capture alongside the WebGL canvas.
  // Omit (or supply empty lists) to render just the map — skipping
  // the overlay rasterisation entirely keeps the render fast. See
  // OverlayConfig in timeline.ts for the full semantic.
  //
  overlayConfig?: OverlayConfig;
  //
  // Mapbox style layer ids to hide for the duration of the render.
  // Useful when a scenario wants to suppress text labels or other
  // style-layer content that would otherwise land in the video.
  // Restored automatically once the render completes.
  //
  hiddenLayerIds?: string[];
  //
  // Per-layer opacity fades. Each entry drives one Mapbox style
  // layer's paint-opacity across a time range (fade-in or fade-
  // out). See LayerFade in timeline.ts.
  //
  layerFades?: LayerFade[];
}

//
// Grand Canyon flight. A 60-second westward cruise along the
// South Rim, starting near Desert View and ending past Hermit
// Rest. Pitch at 55° so we're looking forward into the canyon
// rather than straight down, which gives it the flying-forward
// feel without making terrain-following camera wobble too visible.
//
// Uses three tracks to showcase the editor:
//
//   - Pan (0–60s): continuous westward motion covering the whole
//     shot.
//   - Zoom (0–15s): a gentle zoom-in at the opening, from 12 to
//     12.4. Kept deliberately inside a single Mapbox tile-LOD band
//     (they swap at integer + 0.5) — crossing a band causes the
//     raster and DEM tiles to upgrade in a single frame, which at
//     high pitch reads as the camera suddenly dropping.
//   - Rotate (40–55s): a 15° clockwise nudge near the end — adds a
//     bit of visual dynamism before the fade-out.
//
// Fade in/out of 1 second each for polish.
//
export const GRAND_CANYON: Scenario = {
  name: 'Grand Canyon flight (60s)',
  description: 'Westward flight along the Grand Canyon South Rim, forward-pitched camera, gentle opening zoom and a heading nudge near the end.',
  initialView: {
    center: { lng: -111.82, lat: 36.04 },
    zoom: 12,
    bearing: 260,
    pitch: 55,
  },
  actions: [
    createAction<PanAction>({
      type: 'pan',
      target: { lng: -112.45, lat: 36.05 },
      start: 0,
      end: 60,
      easing: 'ease-in-out',
    }),
    createAction<ZoomAction>({
      type: 'zoom',
      target: 12.4,
      start: 0,
      end: 15,
      easing: 'ease-in-out',
    }),
    createAction<RotateAction>({
      type: 'rotate',
      mode: 'delta',
      deltaDegrees: 15,
      start: 40,
      end: 55,
      easing: 'ease-in-out',
    }),
  ],
  totalSeconds: 60,
  fadeIn: 1,
  fadeOut: 1,
  overlayConfig: { include: [], exclude: [] },
};

//
// Chile flyover — 30-second smooth-spline flight through thirteen
// supplied waypoints in a Patagonian fjord system.
//
// Both tracks (pan and rotate) are marked `smooth: true` on every
// action, which groups them into single Catmull-Rom curves. The
// pan track curves in lng/lat; the bearing track unwraps cumulative
// heading so the 172° → −133° step at waypoint 8→9 (an apparent
// 305° turn, actually +55° forward through south) interpolates
// continuously rather than kinking at the ±180° seam.
//
// Bearing path in the UNWRAPPED domain the spline actually sees:
//
//   109, 109, 75, 57, 115, 115, 139, 172, 227, 273, 299, 310, 310
//
// Segment durations are proportional to great-circle distance
// between consecutive waypoints, so the camera's linear speed
// along the curve stays roughly uniform. Pitch stays at 75° and
// zoom at 15.88 throughout, so only pan and rotate actions are
// needed.
//
// Overlay capture is on for `.mapboxgl-marker` (AquaRisk risk
// markers) and AquaRisk's `asset-names` symbol layer is hidden
// for the render so farm labels don't land on top of the scenery.
//

//
// Per-waypoint spec. `duration` is the time of the pan that
// arrives AT this waypoint from the previous one, in seconds. The
// first entry is the initial view; its `duration` is ignored.
//
interface ChileWaypoint {
  lng: number;
  lat: number;
  bearing: number;
  duration: number;
}

const CHILE_WAYPOINTS: ChileWaypoint[] = [
  { lng: -73.7292, lat: -44.5287, bearing:  109, duration: 0.00 },
  { lng: -73.7090, lat: -44.5340, bearing:  109, duration: 2.25 },
  { lng: -73.6952, lat: -44.5325, bearing:   75, duration: 1.50 },
  { lng: -73.6829, lat: -44.5245, bearing:   57, duration: 1.75 },
  { lng: -73.6715, lat: -44.5284, bearing:  115, duration: 1.25 },
  { lng: -73.6521, lat: -44.5367, bearing:  115, duration: 2.25 },
  { lng: -73.6231, lat: -44.5543, bearing:  139, duration: 4.00 },
  { lng: -73.6161, lat: -44.5753, bearing:  172, duration: 3.00 },
  { lng: -73.6308, lat: -44.5857, bearing: -133, duration: 2.00 },
  { lng: -73.6448, lat: -44.5823, bearing:  -87, duration: 1.50 },
  { lng: -73.6507, lat: -44.5775, bearing:  -61, duration: 1.00 },
  { lng: -73.6778, lat: -44.5581, bearing:  -50, duration: 4.00 },
  { lng: -73.7172, lat: -44.5323, bearing:  -50, duration: 5.50 },
];

//
// Build one pan + one rotate action per waypoint transition,
// stitched end-to-start so the smooth-group grouping rule picks
// them up as a single Catmull-Rom segment on each track. Easing
// is set to linear but is ignored inside smooth groups — it only
// takes effect if the user later unchecks Smooth on an action.
//
function buildChileActions(): Action[] {
  const actions: Action[] = [];
  let t = 0;
  for (let i = 1; i < CHILE_WAYPOINTS.length; i++) {
    const wp = CHILE_WAYPOINTS[i];
    if (!wp) continue;
    const start = t;
    const end = t + wp.duration;
    actions.push(
      createAction<PanAction>({
        type: 'pan',
        target: { lng: wp.lng, lat: wp.lat },
        start,
        end,
        easing: 'linear',
        smooth: true,
      }),
    );
    actions.push(
      createAction<RotateAction>({
        type: 'rotate',
        mode: 'absolute',
        target: wp.bearing,
        start,
        end,
        easing: 'linear',
        smooth: true,
      }),
    );
    t = end;
  }
  return actions;
}

export const CHILE_FLYOVER: Scenario = {
  name: 'Chile-flyover (30s)',
  description: 'Spline-driven 30 s fly-through of a Patagonian fjord system via thirteen waypoints, sweeping clockwise through south into negative bearings and back up the fjord.',
  initialView: {
    center: { lng: -73.7292, lat: -44.5287 },
    zoom: 15.88,
    bearing: 109,
    pitch: 75,
  },
  actions: buildChileActions(),
  totalSeconds: 30,
  fadeIn: 1,
  fadeOut: 1,
  overlayConfig: { include: ['.mapboxgl-marker'], exclude: [] },
  hiddenLayerIds: ['asset-names'],
};

//
// AQUA LWWP flight — 20-second two-part establish-and-sweep shot.
//
//   Segment A (0–8 s): starts in a top-down wide view, zooms in
//   hard (z 9 → 12.74), tilts down to forward-flying pitch (0 →
//   73°), rotates slightly clockwise (0 → 28°) while panning a
//   short hop west. Acts as the "drop in and orient" intro.
//
//   Segment B (8–20 s): long sweeping pan east along the coast at
//   the now-fixed zoom and pitch, with the camera heading turning
//   from 28° to 93° to track the motion. Zoom and pitch each have
//   a single smooth action so they degenerate to a linear lerp in
//   segment A and hold constant through segment B; pan and rotate
//   each form a 3-waypoint smooth group that Catmull-Rom curves
//   through.
//
// Overlay capture and layer hiding are both empty — no HTML
// markers or text labels to suppress on this map.
//
export const AQUA_LWWP_FLIGHT: Scenario = {
  name: 'AQUA-LWWP-flight (20s)',
  description: 'Top-down wide shot that zooms in and tilts to a forward-flying camera, then sweeps east along the coast for 12 s.',
  initialView: {
    center: { lng: -5.6564, lat: 54.6502 },
    zoom: 9.0,
    bearing: 0,
    pitch: 0,
  },
  actions: [
    // Segment A: drop-in establish.
    createAction<PanAction>({
      type: 'pan',
      target: { lng: -5.8533, lat: 54.6468 },
      start: 0,
      end: 8,
      easing: 'linear',
      smooth: true,
    }),
    createAction<ZoomAction>({
      type: 'zoom',
      target: 12.74,
      start: 0,
      end: 8,
      easing: 'linear',
      smooth: true,
    }),
    createAction<PitchAction>({
      type: 'pitch',
      target: 73,
      start: 0,
      end: 8,
      easing: 'linear',
      smooth: true,
    }),
    createAction<RotateAction>({
      type: 'rotate',
      mode: 'absolute',
      target: 28,
      start: 0,
      end: 8,
      easing: 'linear',
      smooth: true,
    }),
    // Segment B: sweeping pan east with matching heading change.
    createAction<PanAction>({
      type: 'pan',
      target: { lng: -5.4263, lat: 54.7107 },
      start: 8,
      end: 20,
      easing: 'linear',
      smooth: true,
    }),
    createAction<RotateAction>({
      type: 'rotate',
      mode: 'absolute',
      target: 93,
      start: 8,
      end: 20,
      easing: 'linear',
      smooth: true,
    }),
  ],
  totalSeconds: 20,
  fadeIn: 1,
  fadeOut: 1,
  overlayConfig: { include: [], exclude: [] },
};

//
// COAST — 32-second three-part shot over the northern Java coast.
//
//   Phase 1 (0–6 s): fly-in from 4.10-zoom altitude south of Java
//   down to the first coastal waypoint at zoom 14, pitch 54°→67°,
//   bearing barely changing (87°→86°). Linear pan and rotate —
//   NOT part of the path's smooth group.
//   Phase 2 (6–26 s): 20-second smooth spline through 12 coastal
//   waypoints at fixed zoom 14 / pitch 67°, with bearing swinging
//   along the direction of travel.
//   Phase 3 (26–32 s): fly-out to 4.24-zoom altitude over the
//   Indian Ocean. Again linear, not in the smooth group.
//
// Why the fly-in / fly-out are deliberately non-smooth: with them
// folded into the group, Catmull-Rom's tangent at the first path
// waypoint is biased by the *initial view* far to the south-east,
// which means during the zoom-in the camera curves toward an
// extrapolation of "through-initial-into-wp1" rather than toward
// wp2. By the time we reach t=6 the camera is off-target and has
// to whip sideways along the ground to land on wp1. Same problem
// in reverse at the end. Taking entry/exit out of the spline
// makes both transitions straight-line pans; the path's spline
// tangent at wp1 is then determined by phantom-reflecting wp2,
// which points naturally along wp1→wp2.
//
// Zoom and pitch use two single-action smooth groups each (in at
// the start, out at the end) rather than three actions with a
// hold in the middle — the latter would produce a visible
// overshoot past 14 / 67° in the middle section, since Catmull-
// Rom tangents at "flat" sections are determined by neighbours
// and don't respect the intent "stay at this value".
//
// Segment durations inside the path are proportional to great-
// circle distance between consecutive waypoints so the camera's
// linear speed along the curve stays roughly uniform. Sum across
// the 11 path segments is 20 s exactly.
//

interface CoastWaypoint {
  lng: number;
  lat: number;
  bearing: number;
  // Duration of the pan that ARRIVES at this waypoint from the
  // previous one, in seconds. The first entry is the initial view;
  // its duration is ignored.
  duration: number;
}

const COAST_WAYPOINTS: CoastWaypoint[] = [
  { lng: 107.8821, lat: -11.8109, bearing:  87, duration: 0.0 },
  { lng: 106.1336, lat:  -6.0211, bearing:  86, duration: 6.0 },
  { lng: 106.2187, lat:  -6.0254, bearing:  61, duration: 2.5 },
  { lng: 106.2704, lat:  -5.9919, bearing:  99, duration: 1.8 },
  { lng: 106.3403, lat:  -5.9895, bearing: 112, duration: 2.0 },
  { lng: 106.4044, lat:  -6.0310, bearing: 138, duration: 2.2 },
  { lng: 106.4711, lat:  -6.0575, bearing: 113, duration: 2.1 },
  { lng: 106.5428, lat:  -6.0286, bearing:  95, duration: 2.2 },
  { lng: 106.6101, lat:  -6.0454, bearing: 112, duration: 2.0 },
  { lng: 106.6496, lat:  -6.0250, bearing:  76, duration: 1.3 },
  { lng: 106.6905, lat:  -6.0349, bearing: 117, duration: 1.2 },
  { lng: 106.7128, lat:  -6.0678, bearing: 153, duration: 1.2 },
  { lng: 106.7412, lat:  -6.1126, bearing: 126, duration: 1.5 },
  { lng: 115.5055, lat: -14.4191, bearing:  87, duration: 6.0 },
];

function buildCoastActions(): Action[] {
  const actions: Action[] = [];
  let t = 0;
  const last = COAST_WAYPOINTS.length - 1;
  for (let i = 1; i < COAST_WAYPOINTS.length; i++) {
    const wp = COAST_WAYPOINTS[i];
    if (!wp) continue;
    const start = t;
    const end = t + wp.duration;
    // Fly-in (i===1, from initial to first path waypoint) and fly-
    // out (i===last, from last path waypoint to end) are NOT part
    // of the smooth group. See the scenario-level comment above
    // for the full reasoning — short version, Catmull-Rom's
    // tangent at the path endpoints gets pulled toward the far-
    // away initial/end views if they're neighbours in the spline,
    // and the camera curves away from the path during zoom.
    const isEntryOrExit = i === 1 || i === last;
    const smooth = !isEntryOrExit;
    // Entry and exit use ease-in-out so the camera decelerates as
    // it lands on the first path waypoint and accelerates cleanly
    // away from the last one. For smooth (spline) actions, easing
    // is ignored by the engine.
    const easing = isEntryOrExit ? 'ease-in-out' : 'linear';
    actions.push(
      createAction<PanAction>({
        type: 'pan',
        target: { lng: wp.lng, lat: wp.lat },
        start,
        end,
        easing,
        smooth,
      }),
    );
    actions.push(
      createAction<RotateAction>({
        type: 'rotate',
        mode: 'absolute',
        target: wp.bearing,
        start,
        end,
        easing,
        smooth,
      }),
    );
    t = end;
  }

  // Zoom in / zoom out as two independent smooth groups so the
  // middle section cleanly holds at 14 instead of overshooting.
  actions.push(
    createAction<ZoomAction>({
      type: 'zoom',
      target: 14.0,
      start: 0,
      end: 6,
      easing: 'linear',
      smooth: true,
    }),
  );
  actions.push(
    createAction<ZoomAction>({
      type: 'zoom',
      target: 4.24,
      start: 26,
      end: 32,
      easing: 'linear',
      smooth: true,
    }),
  );
  // Pitch same pattern: tilt forward into the path, tilt back out.
  actions.push(
    createAction<PitchAction>({
      type: 'pitch',
      target: 67,
      start: 0,
      end: 6,
      easing: 'linear',
      smooth: true,
    }),
  );
  actions.push(
    createAction<PitchAction>({
      type: 'pitch',
      target: 54,
      start: 26,
      end: 32,
      easing: 'linear',
      smooth: true,
    }),
  );

  return actions;
}

export const COAST: Scenario = {
  name: 'COAST (32s)',
  description: 'High-altitude zoom-in over the Indian Ocean, 20-second smooth-spline fly-through along 12 coastal waypoints in northern Java, then zoom-out.',
  initialView: {
    center: { lng: 107.8821, lat: -11.8109 },
    zoom: 4.1,
    bearing: 87,
    pitch: 54,
  },
  actions: buildCoastActions(),
  totalSeconds: 32,
  fadeIn: 1,
  fadeOut: 1,
  overlayConfig: { include: [], exclude: [] },
};

//
// COAST-flyover — 60-second path-only flight across northern Java
// coast. Same 12 path waypoints as COAST, but no fly-in or fly-out
// and no zoom/pitch envelope: the scenario starts sitting at
// waypoint 1 at z 14 / p 67° and ends sitting at waypoint 12 at
// the same parameters. That avoids the long-range endpoint pull
// that makes COAST's zoom-in ugly, at the cost of losing the
// establishing altitude shot.
//
// All 11 pan/rotate action pairs are `smooth: true` — Catmull-Rom
// through the whole path. Segment durations are proportional to
// great-circle distance between consecutive waypoints so linear
// speed stays roughly uniform across the 60 s total.
//

const COAST_FLYOVER_WAYPOINTS: CoastWaypoint[] = [
  { lng: 106.1336, lat: -6.0211, bearing:  86, duration: 0.0 },
  { lng: 106.2187, lat: -6.0254, bearing:  61, duration: 7.4 },
  { lng: 106.2704, lat: -5.9919, bearing:  99, duration: 5.3 },
  { lng: 106.3403, lat: -5.9895, bearing: 112, duration: 6.1 },
  { lng: 106.4044, lat: -6.0310, bearing: 138, duration: 6.6 },
  { lng: 106.4711, lat: -6.0575, bearing: 113, duration: 6.2 },
  { lng: 106.5428, lat: -6.0286, bearing:  95, duration: 6.7 },
  { lng: 106.6101, lat: -6.0454, bearing: 112, duration: 6.0 },
  { lng: 106.6496, lat: -6.0250, bearing:  76, duration: 3.9 },
  { lng: 106.6905, lat: -6.0349, bearing: 117, duration: 3.7 },
  { lng: 106.7128, lat: -6.0678, bearing: 153, duration: 3.4 },
  { lng: 106.7412, lat: -6.1126, bearing: 126, duration: 4.7 },
];

function buildCoastFlyoverActions(): Action[] {
  const actions: Action[] = [];
  let t = 0;
  for (let i = 1; i < COAST_FLYOVER_WAYPOINTS.length; i++) {
    const wp = COAST_FLYOVER_WAYPOINTS[i];
    if (!wp) continue;
    const start = t;
    const end = t + wp.duration;
    actions.push(
      createAction<PanAction>({
        type: 'pan',
        target: { lng: wp.lng, lat: wp.lat },
        start,
        end,
        easing: 'linear',
        smooth: true,
      }),
    );
    actions.push(
      createAction<RotateAction>({
        type: 'rotate',
        mode: 'absolute',
        target: wp.bearing,
        start,
        end,
        easing: 'linear',
        smooth: true,
      }),
    );
    t = end;
  }
  return actions;
}

export const COAST_FLYOVER: Scenario = {
  name: 'COAST-flyover (60s)',
  description: '60-second smooth-spline flight across 12 waypoints along the northern Java coast at fixed zoom 14 / pitch 67°.',
  initialView: {
    center: { lng: 106.1336, lat: -6.0211 },
    zoom: 14,
    bearing: 86,
    pitch: 67,
  },
  actions: buildCoastFlyoverActions(),
  totalSeconds: 60,
  fadeIn: 1,
  fadeOut: 1,
  overlayConfig: { include: [], exclude: [] },
  //
  // Strip bridges, admin boundaries, all labels, and the app's
  // cluster markers so the render shows clean terrain + coastline
  // without Mapbox Standard's line and text chrome fighting for
  // attention in a low-altitude flyover.
  //
  //
  // Start with the satellite raster invisible so the dark vector
  // base + custom polygons drawn by the host app are the first
  // thing the viewer sees. Fade the imagery in across a 2 s
  // window a third of the way through the 60 s flight for a
  // "reveal" effect.
  //
  layerFades: [
    {
      id: 'coast-satellite-fade-in',
      layerId: 'satellite-layer',
      direction: 'in',
      startT: 20,
      endT: 22,
    },
  ],
  hiddenLayerIds: [
    'bridge-path-cycleway-piste',
    'bridge-path',
    'bridge-steps',
    'bridge-pedestrian',
    'bridge-case-simple',
    'bridge-simple',
    'bridge-rail',
    'admin-1-boundary-bg',
    'admin-0-boundary-bg',
    'admin-1-boundary',
    'admin-0-boundary',
    'admin-0-boundary-disputed',
    'road-label-simple',
    'waterway-label',
    'natural-line-label',
    'natural-point-label',
    'water-line-label',
    'water-point-label',
    'poi-label',
    'airport-label',
    'settlement-subdivision-label',
    'settlement-minor-label',
    'settlement-major-label',
    'state-label',
    'country-label',
    'continent-label',
    'regency-outline',
    'regency-label',
    'cluster-circles',
    'cluster-count-labels',
  ],
};

//
// Registered scenarios. Add new ones here and they'll show up as
// buttons in the panel automatically.
//
//
// YOSEMITE-flight — 40-second cinematic dive through Yosemite
// Valley. Three phases:
//
//   Phase 1 (0–4 s): high-altitude approach from the west — drop
//   in, zoom 9.5 → 14, pitch 30° → 70°, slight bearing nudge.
//   Phase 2 (4–32 s): smooth-spline flight east through the valley
//   at fixed zoom 14 / pitch 70° — Bridalveil Falls, El Capitan,
//   Half Dome, then NE toward Tenaya Lake.
//   Phase 3 (32–40 s): climb out over the Sierra crest — zoom 14
//   → 10, pitch 70° → 30°.
//
// Same fly-in/fly-out-NOT-in-spline trick as COAST: keeping the
// approach and exit pans linear avoids the spline's tangent at
// the path endpoints being pulled by the far-away initial/final
// views (which would make the camera curve away from the path
// during the dive-in).
//
// Layers hidden for cinematic clarity: bridges (none in the
// valley but the rule transfers), admin boundaries, and most
// labels. No host-app-specific layers since this scenario is
// designed to be loaded against a vanilla Mapbox Standard map.
//

interface YosemiteWaypoint {
  lng: number;
  lat: number;
  bearing: number;
  duration: number;
}

const YOSEMITE_WAYPOINTS: YosemiteWaypoint[] = [
  { lng: -119.780, lat: 37.710, bearing: 80, duration: 0.0 },   // initial high view
  { lng: -119.710, lat: 37.715, bearing: 88, duration: 4.0 },   // fly-in to path start
  { lng: -119.660, lat: 37.722, bearing: 80, duration: 4.8 },   // approaching Bridalveil
  { lng: -119.638, lat: 37.733, bearing: 75, duration: 2.2 },   // El Capitan
  { lng: -119.605, lat: 37.738, bearing: 78, duration: 3.2 },   // mid-valley
  { lng: -119.575, lat: 37.742, bearing: 70, duration: 2.8 },   // approaching Half Dome
  { lng: -119.535, lat: 37.748, bearing: 60, duration: 3.8 },   // turning NE off Half Dome
  { lng: -119.500, lat: 37.780, bearing: 50, duration: 5.0 },   // climbing toward Tenaya
  { lng: -119.460, lat: 37.825, bearing: 45, duration: 6.2 },   // path end near Tenaya Lake
  { lng: -119.400, lat: 37.860, bearing: 30, duration: 8.0 },   // fly-out over Sierra crest
];

function buildYosemiteActions(): Action[] {
  const actions: Action[] = [];
  let t = 0;
  const last = YOSEMITE_WAYPOINTS.length - 1;
  for (let i = 1; i < YOSEMITE_WAYPOINTS.length; i++) {
    const wp = YOSEMITE_WAYPOINTS[i];
    if (!wp) continue;
    const start = t;
    const end = t + wp.duration;
    const isEntryOrExit = i === 1 || i === last;
    const smooth = !isEntryOrExit;
    const easing = isEntryOrExit ? 'ease-in-out' : 'linear';
    actions.push(
      createAction<PanAction>({
        type: 'pan',
        target: { lng: wp.lng, lat: wp.lat },
        start,
        end,
        easing,
        smooth,
      }),
    );
    actions.push(
      createAction<RotateAction>({
        type: 'rotate',
        mode: 'absolute',
        target: wp.bearing,
        start,
        end,
        easing,
        smooth,
      }),
    );
    t = end;
  }

  // Zoom + pitch envelopes: separate "in" and "out" smooth groups
  // so the middle holds at 14 / 70° instead of overshooting.
  actions.push(
    createAction<ZoomAction>({
      type: 'zoom',
      target: 14.0,
      start: 0,
      end: 4,
      easing: 'linear',
      smooth: true,
    }),
  );
  actions.push(
    createAction<ZoomAction>({
      type: 'zoom',
      target: 10.0,
      start: 32,
      end: 40,
      easing: 'linear',
      smooth: true,
    }),
  );
  actions.push(
    createAction<PitchAction>({
      type: 'pitch',
      target: 70,
      start: 0,
      end: 4,
      easing: 'linear',
      smooth: true,
    }),
  );
  actions.push(
    createAction<PitchAction>({
      type: 'pitch',
      target: 30,
      start: 32,
      end: 40,
      easing: 'linear',
      smooth: true,
    }),
  );

  return actions;
}

export const YOSEMITE_FLIGHT: Scenario = {
  name: 'Yosemite flight (40s)',
  description: '40-second dive into Yosemite Valley from the west, smooth flight past El Capitan and Half Dome, climb out over the Sierra crest near Tenaya Lake.',
  initialView: {
    center: { lng: -119.780, lat: 37.710 },
    zoom: 9.5,
    bearing: 80,
    pitch: 30,
  },
  actions: buildYosemiteActions(),
  totalSeconds: 40,
  fadeIn: 1,
  fadeOut: 1,
  overlayConfig: { include: [], exclude: [] },
  hiddenLayerIds: [
    'bridge-path-cycleway-piste',
    'bridge-path',
    'bridge-steps',
    'bridge-pedestrian',
    'bridge-case-simple',
    'bridge-simple',
    'bridge-rail',
    'admin-1-boundary-bg',
    'admin-0-boundary-bg',
    'admin-1-boundary',
    'admin-0-boundary',
    'admin-0-boundary-disputed',
    'road-label-simple',
    'waterway-label',
    'natural-line-label',
    'natural-point-label',
    'water-line-label',
    'water-point-label',
    'poi-label',
    'airport-label',
    'settlement-subdivision-label',
    'settlement-minor-label',
    'settlement-major-label',
    'state-label',
    'country-label',
    'continent-label',
  ],
};

export const SCENARIOS: Scenario[] = [
  GRAND_CANYON,
  CHILE_FLYOVER,
  AQUA_LWWP_FLIGHT,
  COAST,
  COAST_FLYOVER,
  YOSEMITE_FLIGHT,
];
